import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { CoupleSettings, TimeElapsed, GardenAtmosphere, TimeOfDay, WeatherType, GardenFlower, GardenWateringState } from '../types';
import { resolveTulipColor } from '../utils/tulipColorUtils';
import { calculateHydrationState, getEffectiveCanWaterLevel, WATER_PER_POUR, REFILL_CYCLE_MS } from '../utils/wateringUtils';
import { recordWateringInDb } from '../services/firebaseDb';
import { DEFAULT_SETTINGS } from '../data/initialData';
import { FlowerGraphic } from './FlowerGraphic';
import { CounterHUD } from './CounterHUD';
import { GardenSky } from './GardenSky';
import { GardenWeatherEffects } from './GardenWeatherEffects';
import { GardenGround } from './GardenGround';
import { GardenButterflies } from './GardenButterflies';
import { GardenFireflies } from './GardenFireflies';
import { ManualGardenCan } from './ManualGardenCan';
import { calculateCelestialState, getMinuteFromDate, getDefaultMinuteForPhase } from '../utils/skyCycle';

interface InteractiveGardenProps {
  settings: CoupleSettings;
  timeElapsed: TimeElapsed;
  isAdmin?: boolean;
  onOpenSettings: () => void;
  onGoToBlog: () => void;
  onWaterGarden?: (updatedWatering?: Partial<GardenWateringState>) => Promise<void> | void;
}

// Derive atmosphere from couple settings
function getAtmosphereFromSettings(settings: CoupleSettings): GardenAtmosphere {
  const mode = settings.atmosphereMode || 'auto';

  if (mode === 'manual') {
    const timeOfDay = settings.manualTimeOfDay || 'day';
    const minute =
      settings.manualMinuteOfDay !== undefined
        ? settings.manualMinuteOfDay
        : getDefaultMinuteForPhase(timeOfDay);
    const weather = settings.manualWeather || 'sunny';
    const celestial = calculateCelestialState(minute, weather);

    return {
      timeOfDay,
      weather,
      autoRealTime: false,
      isTimelapse: false,
      autoWeather: false,
      sunPosition: celestial.sunX / 100,
      minuteOfDay: minute,
    };
  }

  if (mode === 'timelapse') {
    const currentMin = getMinuteFromDate();
    const celestial = calculateCelestialState(currentMin, 'sunny');
    return {
      timeOfDay: celestial.phase,
      weather: 'sunny',
      autoRealTime: false,
      isTimelapse: true,
      autoWeather: true,
      sunPosition: celestial.sunX / 100,
      minuteOfDay: currentMin,
    };
  }

  // Default 'auto': Sincronización automática con reloj real del dispositivo y clima dinámico
  const currentMin = getMinuteFromDate();
  const celestial = calculateCelestialState(currentMin, 'sunny');
  return {
    timeOfDay: celestial.phase,
    weather: 'sunny',
    autoRealTime: true,
    isTimelapse: false,
    autoWeather: true,
    sunPosition: celestial.sunX / 100,
    minuteOfDay: currentMin,
  };
}

export const InteractiveGarden: React.FC<InteractiveGardenProps> = React.memo(({
  settings,
  timeElapsed,
  isAdmin = false,
  onOpenSettings,
  onGoToBlog,
  onWaterGarden,
}) => {
  const [atmosphere, setAtmosphere] = useState<GardenAtmosphere>(() => getAtmosphereFromSettings(settings));

  // Sync atmosphere state whenever settings change (e.g. from Admin Panel)
  useEffect(() => {
    setAtmosphere(getAtmosphereFromSettings(settings));
  }, [
    settings.atmosphereMode,
    settings.manualTimeOfDay,
    settings.manualWeather,
    settings.manualMinuteOfDay,
  ]);

  // 1. Real-time Clock Sync Loop (Synchronizes automatically with device local clock)
  useEffect(() => {
    if (!atmosphere.autoRealTime) return;

    const interval = setInterval(() => {
      const realMin = getMinuteFromDate();
      const celestial = calculateCelestialState(realMin, atmosphere.weather);
      setAtmosphere((prev) => {
        if (!prev.autoRealTime) return prev;
        return {
          ...prev,
          minuteOfDay: realMin,
          timeOfDay: celestial.phase,
          sunPosition: celestial.sunX / 100,
        };
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [atmosphere.autoRealTime, atmosphere.weather]);

  // 2. Day-Night Timelapse Continuous Cycle Simulation (if configured from Admin)
  useEffect(() => {
    if (!atmosphere.isTimelapse) return;

    const interval = setInterval(() => {
      setAtmosphere((prev) => {
        if (!prev.isTimelapse) return prev;
        const currentMin = prev.minuteOfDay ?? getMinuteFromDate();
        const nextMin = (currentMin + 2.5) % 1440;
        const celestial = calculateCelestialState(nextMin, prev.weather);

        return {
          ...prev,
          minuteOfDay: nextMin,
          timeOfDay: celestial.phase,
          sunPosition: celestial.sunX / 100,
        };
      });
    }, 60);

    return () => clearInterval(interval);
  }, [atmosphere.isTimelapse]);

  // 3. Dynamic Real-Life Weather Engine (Realistic Weather Lifespans when in auto mode)
  useEffect(() => {
    if (atmosphere.autoWeather === false) return;

    let timeoutId: ReturnType<typeof setTimeout>;

    const scheduleNextWeather = () => {
      const rand = Math.random();
      let nextWeather: WeatherType = 'sunny';
      let durationMs = 90000;

      if (rand < 0.62) {
        nextWeather = 'sunny';
        durationMs = 80000 + Math.random() * 80000;
      } else if (rand < 0.86) {
        nextWeather = 'cloudy';
        durationMs = 45000 + Math.random() * 45000;
      } else {
        nextWeather = 'rainy';
        durationMs = 35000 + Math.random() * 35000;
      }

      timeoutId = setTimeout(() => {
        setAtmosphere((prev) => {
          if (prev.autoWeather === false) return prev;
          return {
            ...prev,
            weather: nextWeather,
          };
        });
        scheduleNextWeather();
      }, durationMs);
    };

    scheduleNextWeather();

    return () => clearTimeout(timeoutId);
  }, [atmosphere.autoWeather]);

  // Compute current celestial state
  const currentMinute = atmosphere.minuteOfDay ?? getMinuteFromDate();
  const celestial = useMemo(
    () => calculateCelestialState(currentMinute, atmosphere.weather),
    [currentMinute, atmosphere.weather]
  );

  // Sun / Moon click interaction
  const handleSunOrMoonClick = useCallback((_e: React.MouseEvent) => {
    // Silent interaction
  }, []);

  // Cloud click interaction
  const handleCloudClick = useCallback((_e: React.MouseEvent) => {
    // Silent interaction
  }, []);

  // Hydration state and watering can real-time tracking (1-second precision heartbeat)
  const [currentTimestamp, setCurrentTimestamp] = useState(() => Date.now());
  const [isWatering, setIsWatering] = useState(false);
  const [targetFlower, setTargetFlower] = useState<number | null>(null);
  const gardenContainerRef = useRef<HTMLDivElement>(null);

  // Active optimistic garden watering state (ensures 0ms lag and protects against network delays)
  const [localWatering, setLocalWatering] = useState<GardenWateringState>(() => {
    let cached: any = {};
    if (typeof window !== 'undefined') {
      try {
        const localW = localStorage.getItem('couple_garden_watering');
        if (localW) cached = JSON.parse(localW);
      } catch {}
    }
    return {
      ...DEFAULT_SETTINGS.gardenWatering,
      ...cached,
      ...(settings.gardenWatering || {}),
    };
  });

  // Reconcile incoming settings updates from Firestore or parent App
  useEffect(() => {
    if (settings.gardenWatering) {
      setLocalWatering((prev) => {
        const prevTime = prev.lastWateredAt ? Date.parse(prev.lastWateredAt) : 0;
        const incomingTime = settings.gardenWatering?.lastWateredAt
          ? Date.parse(settings.gardenWatering.lastWateredAt)
          : 0;
        if (incomingTime >= prevTime) {
          return {
            ...prev,
            ...settings.gardenWatering,
          };
        }
        return prev;
      });
    }
  }, [settings.gardenWatering]);

  // Synchronize across windows/tabs or components via custom event
  useEffect(() => {
    const handleSync = (e: Event) => {
      const customEvent = e as CustomEvent<GardenWateringState>;
      if (customEvent.detail) {
        setLocalWatering((prev) => ({
          ...prev,
          ...customEvent.detail,
        }));
      }
    };
    window.addEventListener('couple_garden_watering_changed', handleSync);
    return () => window.removeEventListener('couple_garden_watering_changed', handleSync);
  }, []);

  useEffect(() => {
    const updateTime = () => {
      setCurrentTimestamp(Date.now());
    };

    updateTime();
    const timer = setInterval(updateTime, 1000);

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        updateTime();
      }
    };

    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('focus', updateTime);

    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('focus', updateTime);
    };
  }, []);

  const hydrationState = useMemo(() => {
    return calculateHydrationState(
      localWatering.lastWateredAt,
      localWatering.wateringIntervalDays || 3
    );
  }, [
    localWatering.lastWateredAt,
    localWatering.wateringIntervalDays,
    currentTimestamp,
  ]);

  // Individual hydration states for each of the 4 individual tulips
  const flowerHydrations = useMemo(() => {
    const dates = localWatering.flowerWateredAt;
    const fallbackDate = localWatering.lastWateredAt;
    const interval = localWatering.wateringIntervalDays || 3;
    return [0, 1, 2, 3].map((idx) => {
      const date = (dates && dates[idx]) || fallbackDate;
      return calculateHydrationState(date, interval);
    });
  }, [
    localWatering.flowerWateredAt,
    localWatering.lastWateredAt,
    localWatering.wateringIntervalDays,
    currentTimestamp,
  ]);

  // Watering Can Capacity & Real-Time 24h Refill Engine
  const canWaterStatus = useMemo(() => {
    return getEffectiveCanWaterLevel(localWatering, currentTimestamp);
  }, [
    localWatering,
    currentTimestamp,
  ]);

  // Synchronize automatic 24-hour refill back to Firestore when countdown finishes
  useEffect(() => {
    if (canWaterStatus.needsDailyReset) {
      const resetWatering: Partial<GardenWateringState> = {
        canWaterLevel: 100,
        lastWaterRefillDate: canWaterStatus.todayStr,
        lastCanUsedAt: null as any,
      };
      setLocalWatering((prev) => ({
        ...prev,
        ...resetWatering,
      }));
      recordWateringInDb(resetWatering).catch((err) => {
        console.error('Error auto-syncing 24h water refill to Firestore:', err);
      });
    }
  }, [canWaterStatus.needsDailyReset, canWaterStatus.todayStr]);

  // Master Watering Trigger (Optimistic 0ms update + database persistence)
  const handleWaterGarden = useCallback(
    async (flowerIdx?: number) => {
      if (isWatering) return;

      if (canWaterStatus.isEmpty) {
        return;
      }

      setIsWatering(true);
      if (typeof flowerIdx === 'number') {
        setTargetFlower(flowerIdx);
      } else {
        setTargetFlower(null);
      }

      const nowIso = new Date().toISOString();
      const currentLevel = canWaterStatus.canWaterLevel;
      const deduction = typeof flowerIdx === 'number' ? WATER_PER_POUR : Math.max(WATER_PER_POUR, currentLevel);
      const nextLevel = Math.max(0, currentLevel - deduction);

      const existingFlowers = localWatering.flowerWateredAt || [
        nowIso,
        nowIso,
        nowIso,
        nowIso,
      ];
      const nextFlowers = [...existingFlowers];
      if (typeof flowerIdx === 'number' && flowerIdx >= 0 && flowerIdx < 4) {
        nextFlowers[flowerIdx] = nowIso;
      } else {
        for (let i = 0; i < 4; i++) {
          nextFlowers[i] = nowIso;
        }
      }

      // Active 24-hour cycle check
      const prevUsedAt = localWatering.lastCanUsedAt;
      const isPrevCycleActive = prevUsedAt && (Date.now() - Date.parse(prevUsedAt) < REFILL_CYCLE_MS);
      const startCountdownAt = isPrevCycleActive ? prevUsedAt : nowIso;

      const updatedWatering: GardenWateringState = {
        ...localWatering,
        lastWateredAt: nowIso,
        wateredCount: (localWatering.wateredCount || 0) + 1,
        canWaterLevel: nextLevel,
        lastWaterRefillDate: canWaterStatus.todayStr,
        lastCanUsedAt: nextLevel >= 100 ? (null as any) : startCountdownAt,
        flowerWateredAt: nextFlowers,
      };

      // 1. INSTANT OPTIMISTIC STATE UPDATE: Flower perks up immediately and counter decrements
      setLocalWatering(updatedWatering);

      // 2. Notify parent App component
      if (onWaterGarden) {
        try {
          await onWaterGarden(updatedWatering);
        } catch {}
      }

      // 3. Persist to Firestore and localStorage in background
      try {
        await recordWateringInDb(updatedWatering);
      } catch (err) {
        console.warn('Background sync notice (watering preserved locally):', err);
      } finally {
        setTimeout(() => {
          setIsWatering(false);
          setTargetFlower(null);
        }, typeof flowerIdx === 'number' ? 2400 : 3600);
      }
    },
    [
      isWatering,
      canWaterStatus.isEmpty,
      canWaterStatus.canWaterLevel,
      canWaterStatus.todayStr,
      localWatering,
      onWaterGarden,
    ]
  );

  // Manual instant refill action
  const handleRefillCan = useCallback(async () => {
    const refilled: Partial<GardenWateringState> = {
      canWaterLevel: 100,
      lastWaterRefillDate: canWaterStatus.todayStr,
      lastCanUsedAt: null as any,
    };
    setLocalWatering((prev) => ({
      ...prev,
      ...refilled,
    }));
    if (onWaterGarden) {
      try {
        await onWaterGarden(refilled);
      } catch {}
    }
    await recordWateringInDb(refilled).catch(() => {});
  }, [canWaterStatus.todayStr, onWaterGarden]);

  // Flower Click interaction: click a flower to water it manually!
  const handleFlowerClick = useCallback(
    (idx: number) => {
      if (!isWatering && !canWaterStatus.isEmpty) {
        handleWaterGarden(idx);
      }
    },
    [isWatering, canWaterStatus.isEmpty, handleWaterGarden]
  );

  const displayFlowers = useMemo<GardenFlower[]>(() => [
    { id: 'gf-1', flowerType: 'golden-tulip', growthStage: 'bloom' },
    { id: 'gf-2', flowerType: 'golden-tulip', growthStage: 'bloom' },
    { id: 'gf-3', flowerType: 'golden-tulip', growthStage: 'bloom' },
    { id: 'gf-4', flowerType: 'golden-tulip', growthStage: 'bloom' },
  ], []);

  const tulipColors = useMemo(() => {
    return [0, 1, 2, 3].map((idx) => resolveTulipColor(settings.tulipSettings, idx).colorValue);
  }, [settings.tulipSettings]);

  return (
    <div
      ref={gardenContainerRef}
      id="interactive-garden-root"
      className="relative w-full max-w-full min-h-[calc(100dvh-3.5rem)] sm:min-h-[calc(100dvh-4rem)] flex flex-col justify-between overflow-hidden select-none"
      style={
        {
          '--garden-tint': celestial.gardenTint,
          '--soil-1': celestial.soilGradient[0],
          '--soil-2': celestial.soilGradient[1],
          '--soil-3': celestial.soilGradient[2],
        } as React.CSSProperties
      }
    >
      {/* 1. DYNAMIC CELESTIAL SKY (Amanecer, Día, Atardecer, Noche con fórmulas matemáticas exactas) */}
      <GardenSky
        timeOfDay={celestial.phase}
        weather={atmosphere.weather}
        minuteOfDay={currentMinute}
        sunPosition={celestial.sunX / 100}
        onSunOrMoonClick={handleSunOrMoonClick}
        onCloudClick={handleCloudClick}
      />

      {/* 2. REALISTIC WEATHER & ATMOSPHERE PARTICLES (Rain, Pollen, Clouds) */}
      <GardenWeatherEffects
        timeOfDay={celestial.phase}
        weather={atmosphere.weather}
        onCloudClick={handleCloudClick}
      />

      {/* 3. ROMANTIC BIOLUMINESCENT FIREFLIES (Luces suaves durante Atardecer y Noche) */}
      <GardenFireflies
        count={celestial.firefliesCount}
        timeOfDay={celestial.phase}
        weather={atmosphere.weather}
      />

      {/* 4. REALISTIC BOTANICAL BUTTERFLIES (Active during Day, Dawn, Sunset) */}
      <GardenButterflies isNight={celestial.phase === 'night'} />

      {/* 5. INTERACTIVE FLOWERS & MEADOW GROUND SECTION */}
      <div className="relative w-full max-w-full mt-auto z-20 flex flex-col items-center">
        {/* ROW OF REALISTIC TULIPS (Rooted naturally into the meadow) */}
        <div className="relative w-full max-w-2xl px-3 sm:px-6 flex items-end justify-between z-20 -mb-8 sm:-mb-10">
          {displayFlowers.map((flower, idx) => (
            <div
              key={flower.id}
              id={`flower-column-${idx}`}
              className="flex-1 flex justify-center max-w-[85px] xs:max-w-[105px] sm:max-w-[140px]"
            >
              <div
                id={`flower-anchor-${idx}`}
                className="origin-bottom cursor-pointer group"
                title={`Flor ${idx + 1} • ${flowerHydrations[idx]?.hydrationPercent || 100}% hidratada`}
              >
                <FlowerGraphic
                  flower={flower}
                  index={idx}
                  timeOfDay={celestial.phase}
                  weather={atmosphere.weather}
                  colorValue={tulipColors[idx]}
                  hydrationState={flowerHydrations[idx]}
                  isBeingWatered={isWatering && (targetFlower === null || targetFlower === idx)}
                  onClick={() => handleFlowerClick(idx)}
                />
              </div>
            </div>
          ))}
        </div>

        {/* DEDICATED MEADOW RESTING STATION FOR THE WATERING CAN:
            Positioned cleanly on the lawn beside Flower 4, safely above the Counter HUD and away from screen borders! */}
        <div
          id="garden-watering-can-dock"
          className="absolute right-5 xs:right-7 sm:right-10 md:right-16 bottom-48 sm:bottom-52 z-35 pointer-events-none w-20 h-16 flex flex-col items-center justify-end"
        >
          {/* Subtle soft lawn shadow beneath watering can resting station */}
          <div className="w-16 sm:w-20 h-3.5 sm:h-4 rounded-full bg-black/30 blur-xs transform translate-y-1" />
        </div>

        {/* 3D REALISTIC CONTINUOUS LAWN GROUND */}
        <div className="relative w-full z-10">
          <GardenGround
            timeOfDay={celestial.phase}
            weather={atmosphere.weather}
          />
        </div>

        {/* 7. RELATIONSHIP COUNTER HUD OVER MEADOW */}
        <div className="relative w-full max-w-full z-30 pb-20 sm:pb-5 pt-1 -mt-8 sm:-mt-10 md:-mt-12">
          <CounterHUD
            timeElapsed={timeElapsed}
            settings={settings}
            isAdmin={isAdmin}
            onOpenSettings={onOpenSettings}
          />
        </div>
      </div>

      {/* 8. MASTER MANUAL WATERING CAN (Can move freely across whole screen and waters from above flowers) */}
      <ManualGardenCan
        gardenRef={gardenContainerRef}
        canWaterLevel={canWaterStatus.canWaterLevel}
        isWatering={isWatering}
        targetFlowerIndex={targetFlower}
        countdownText={canWaterStatus.countdownTextSpanish}
        isAdmin={isAdmin}
        onPourWater={(idx) => handleWaterGarden(idx)}
        onRefillCan={handleRefillCan}
      />
    </div>
  );
});

InteractiveGarden.displayName = 'InteractiveGarden';
